/**
 * Per-article indexing feedback strip. Sits at the top of the editor when
 * we're viewing a published article. Shows three submission outcomes
 * (Google Indexing API, IndexNow, GSC sitemap) + the current Google
 * verdict, each rendered as a small chip with hover-for-detail.
 */

import { useEffect, useState } from 'react'
import { api } from '../lib/api.ts'

interface IndexingDetail {
  slug: string
  url: string
  publishedAt: number | null
  indexingApiAt: number | null
  indexingApiOk: number | null
  indexingApiError: string | null
  indexNowAt: number | null
  indexNowOk: number | null
  indexNowError: string | null
  sitemapSubmitAt: number | null
  sitemapSubmitOk: number | null
  sitemapSubmitError: string | null
  inspectedAt: number | null
  state: 'indexed' | 'discovered' | 'excluded' | 'unknown'
  verdict: string | null
  coverageState: string | null
  canonicalGoogle: string | null
  canonicalUser: string | null
}

interface Props {
  siteId: string
  slug: string
}

export function IndexingBadgeBar({ siteId, slug }: Props) {
  const [data, setData] = useState<IndexingDetail | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api<IndexingDetail>(`/api/sites/${siteId}/indexing/${slug}`)
      .then((res) => !cancelled && setData(res))
      .catch((e) => !cancelled && setErr((e as Error).message))
    return () => { cancelled = true }
  }, [siteId, slug])

  if (err) return null // best-effort surface; don't error the whole editor
  if (!data) return null
  // If never published via ContentHub AND not in GSC, hide entirely.
  if (!data.publishedAt && !data.inspectedAt) return null

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        flexWrap: 'wrap',
        background: 'var(--bg-muted)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: '0.4rem 0.65rem',
        marginBottom: '1rem',
        fontSize: '0.75rem',
      }}
    >
      <span style={{ color: 'var(--text-faint)', fontWeight: 500 }}>Indexing:</span>
      <Chip
        label="Indexing API"
        ok={data.indexingApiOk}
        at={data.indexingApiAt}
        err={data.indexingApiError}
      />
      <Chip
        label="IndexNow"
        ok={data.indexNowOk}
        at={data.indexNowAt}
        err={data.indexNowError}
      />
      <Chip
        label="Sitemap"
        ok={data.sitemapSubmitOk}
        at={data.sitemapSubmitAt}
        err={data.sitemapSubmitError}
      />
      <Verdict state={data.state} verdict={data.verdict} inspectedAt={data.inspectedAt} />
      {data.canonicalGoogle &&
        data.canonicalUser &&
        stripQ(data.canonicalGoogle) !== stripQ(data.canonicalUser) && (
          <span
            title={`Google chose: ${data.canonicalGoogle}\nYou declared: ${data.canonicalUser}`}
            style={{
              padding: '0.15rem 0.45rem',
              borderRadius: '999px',
              background: '#fef2f2',
              color: 'var(--danger)',
              fontWeight: 500,
            }}
          >
            ⚠ canonical mismatch
          </span>
        )}
      <a
        href={data.url}
        target="_blank"
        rel="noopener"
        style={{ marginLeft: 'auto', color: 'var(--text-faint)', textDecoration: 'underline' }}
        title="Open live article in new tab"
      >
        view live ↗
      </a>
    </div>
  )
}

function Chip({
  label,
  ok,
  at,
  err,
}: {
  label: string
  ok: number | null
  at: number | null
  err: string | null
}) {
  if (at === null) {
    return (
      <span
        title="Never submitted via ContentHub for this article"
        style={chipStyle('#f3f4f6', 'var(--text-faint)')}
      >
        {label}: —
      </span>
    )
  }
  if (ok === 1) {
    return (
      <span
        title={`Submitted ${new Date(at * 1000).toLocaleString()}`}
        style={chipStyle('#f0fdf4', 'var(--ok)')}
      >
        ✓ {label}
      </span>
    )
  }
  return (
    <span
      title={err ? `Failed ${new Date(at * 1000).toLocaleString()}: ${err}` : `Failed ${new Date(at * 1000).toLocaleString()}`}
      style={chipStyle('#fef2f2', 'var(--danger)')}
    >
      ✗ {label}
    </span>
  )
}

function Verdict({
  state,
  verdict,
  inspectedAt,
}: {
  state: 'indexed' | 'discovered' | 'excluded' | 'unknown'
  verdict: string | null
  inspectedAt: number | null
}) {
  const map = {
    indexed: { bg: '#f0fdf4', fg: 'var(--ok)', icon: '✓', label: 'Google: indexed' },
    discovered: { bg: '#fffbeb', fg: 'var(--warn)', icon: '⏳', label: 'Google: discovered' },
    excluded: { bg: '#fef2f2', fg: 'var(--danger)', icon: '✗', label: 'Google: excluded' },
    unknown: { bg: '#f3f4f6', fg: 'var(--text-faint)', icon: '?', label: 'Google: unknown' },
  }[state]
  const titleParts = [verdict, inspectedAt ? `inspected ${new Date(inspectedAt * 1000).toLocaleString()}` : null]
    .filter(Boolean)
    .join(' · ')
  return (
    <span title={titleParts || undefined} style={chipStyle(map.bg, map.fg)}>
      {map.icon} {map.label}
    </span>
  )
}

function chipStyle(bg: string, fg: string) {
  return {
    padding: '0.15rem 0.5rem',
    borderRadius: '999px',
    background: bg,
    color: fg,
    fontWeight: 500,
    whiteSpace: 'nowrap' as const,
  }
}

function stripQ(u: string): string {
  return (u.split('?')[0] || u).replace(/\/$/, '')
}
